import { useNavigate } from "react-router-dom";
import { MobileFrame } from "@/components/MobileFrame";
import { Card, Icon, Section } from "@/components/primitives";

// What each FIG verdict means, surfaced as a standalone reference page so
// the meanings aren't scattered across the verdictLabelMap in different
// pages. Linked from Settings → Tips.

// Three theme-safe pill styles. The previous direct uses of --marigold,
// --sand, --paper-3, and --cream don't reliably contrast across themes —
// only --ink+--ink-fg and --rust+--rust-fg are guaranteed.
//
//   rust-filled : positive verdicts (FIG Verified, Likely Vintage)
//   ink-filled  : negative verdicts (Unlikely / Not Vintage)
//   ink-outline : neutral verdicts (Inconclusive, More Photos, Coming Soon)
type PillKind = "rust-filled" | "ink-filled" | "ink-outline";

interface VerdictRow {
  label: string;
  short: string;
  long: string;
  pill: PillKind;
}

const VERDICTS: VerdictRow[] = [
  {
    label: "FIG Verified",
    short: "High confidence — era, brand, and materials all line up.",
    long: "The model found strong agreement across the brand label, construction signals, and material reads. Brand triage either approved or didn't flag the result. Treat this as a confident go-ahead to list at the suggested price band.",
    pill: "rust-filled",
  },
  {
    label: "Likely Vintage",
    short: "Passing read with some uncertainty — usually medium confidence.",
    long: "Most signals point to authentic vintage but at least one came back weak (worn label, unclear stitching, partial brand visibility). The price band will lean slightly conservative; a better label or construction shot on a re-scan can lift this to FIG Verified.",
    pill: "rust-filled",
  },
  {
    label: "Inconclusive",
    short: "FIG couldn't reach a confident verdict either way.",
    long: "Signals contradicted each other or weren't strong enough to commit. Common when the construction looks period-correct but the label is obscured, or vice versa. Try better photos of the label + interior construction before listing — or hand-verify if it's a high-value piece.",
    pill: "ink-outline",
  },
  {
    label: "More Photos Needed",
    short: "Not enough evidence to call it — re-scan with the missing angle.",
    long: "Typical reasons: no label captured, no construction detail, blurry hero shot, or a flagged category (fur / leather / luxury) needs an extra evidence photo. The verdict isn't a fail — it's a request for more input. Re-scanning doesn't count against your credit allowance.",
    pill: "ink-outline",
  },
  {
    label: "Coming Soon",
    short: "Category isn't on FIG's verification roster yet.",
    long: "Some categories (some watches, fine jewelry, certain accessories) need specialist authentication beyond FIG's current scope. We don't return a verdict and your scan credit isn't used. Hand-verify with a category specialist before listing.",
    pill: "ink-outline",
  },
  {
    label: "Unlikely Vintage",
    short: "Multiple signals suggest the piece isn't authentic vintage.",
    long: "Construction, label, or materials read modern when the claim was vintage. Sometimes the brand is on FIG's deny list (mall budget / fast fashion / late-era licensing). Read the Inconsistent notes for which signals failed and consider hand-inspection if you got the piece from a trusted source.",
    pill: "ink-filled",
  },
  {
    label: "Not Vintage",
    short: "High-confidence fail — strong evidence the piece is modern or fast-fashion.",
    long: "Reserved for clear cases: the brand is in the deny list and the era reads modern, or construction signals (modern fasteners, synthetic blends, machine-uniform stitching) firmly contradict a vintage claim. Don't list as vintage; consider re-categorising or returning the piece.",
    pill: "ink-filled",
  },
];

function pillStyle(kind: PillKind): React.CSSProperties {
  if (kind === "rust-filled") {
    return { background: "hsl(var(--rust))", color: "hsl(var(--rust-fg))", border: "none" };
  }
  if (kind === "ink-filled") {
    return { background: "hsl(var(--ink))", color: "hsl(var(--ink-fg))", border: "none" };
  }
  return { background: "transparent", color: "hsl(var(--ink))", border: "1px solid hsl(var(--ink))" };
}

export default function VerdictLegend() {
  const nav = useNavigate();

  return (
    <MobileFrame>
      <div className="paper-bg" style={{ minHeight: "100vh", paddingBottom: 60 }}>
        <div style={{
          padding: "12px 20px 0",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <button
            onClick={() => nav(-1)}
            style={{
              background: "none", border: "none", padding: 6, cursor: "pointer",
              color: "hsl(var(--ink))",
            }}
            aria-label="Back"
          >
            {Icon.chev(18, "l")}
          </button>
          <span className="caps">Verdict legend</span>
          <div style={{ width: 30 }} />
        </div>

        <Section pad={14} eyebrow="Reference">
          <div style={{
            background: "hsl(var(--ink))",
            color: "hsl(var(--cream))",
            padding: "20px 22px 18px",
          }}>
            <div style={{
              fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 900,
              fontSize: 30, lineHeight: 1.05, letterSpacing: "-0.02em",
            }}>
              What every FIG verdict means.
            </div>
            <div style={{
              fontFamily: "'Fraunces', 'Playfair Display', serif", fontStyle: "italic",
              fontSize: 14, color: "hsl(var(--cream))", opacity: 0.85,
              marginTop: 8, lineHeight: 1.4,
            }}>
              FIG returns one of seven verdicts. Each carries a different signal
              about how confident the result is and what to do next.
            </div>
          </div>
        </Section>

        <Section pad={14}>
          {VERDICTS.map((v) => (
            <Card key={v.label} style={{ padding: 14, marginBottom: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <span style={{
                  ...pillStyle(v.pill),
                  padding: "4px 10px", borderRadius: 4,
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 10, letterSpacing: "0.12em", textTransform: "uppercase",
                  fontWeight: 700,
                }}>{v.label}</span>
              </div>
              <div style={{
                fontFamily: "'Fraunces', 'Playfair Display', serif",
                fontWeight: 700, fontSize: 15, color: "hsl(var(--ink))",
                lineHeight: 1.3, marginBottom: 6,
              }}>
                {v.short}
              </div>
              <div style={{
                fontFamily: "'Fraunces', 'Playfair Display', serif",
                fontSize: 13, color: "hsl(var(--ink-2))", lineHeight: 1.5,
              }}>
                {v.long}
              </div>
            </Card>
          ))}

          <div style={{
            marginTop: 14,
            padding: "14px 14px",
            background: "hsl(var(--cream))",
            border: "1px dashed hsl(var(--sand-deep))",
            fontFamily: "'Fraunces', 'Playfair Display', serif",
            fontSize: 13, color: "hsl(var(--ink-2))",
            lineHeight: 1.5,
          }}>
            <strong>Hand-verify when stakes are high.</strong>{" "}
            For pieces over $1000, heavily counterfeited brands (Hermès, Chanel,
            LV, Rolex, Cartier), or pre-1975 designer pieces, FIG flags an
            advisory to confirm the verdict in person before listing — photos
            have limits.
          </div>
        </Section>
      </div>
    </MobileFrame>
  );
}
